// src/components/intake/SpecsForm.jsx
//
// The book specs step: the contact email, then one field per pricing category,
// then the estimate. The categories, options and limits come from
// GET /api/quotes/pricing, the table admins edit, so this form offers exactly
// what /quote offers. The mock-up's own option list and price formula were not
// ported, for the same reason QuotePage did not port them.

import React, { useEffect, useState } from 'react';
import { Box, MenuItem, Typography } from '@mui/material';
import Field from '../common/Field';
import { tokens } from '../../theme';
import { isValidEmail, money } from '../../utils/intake';
import { SP, FONT } from './intakeSx';

// A number field that lets the author type freely and only settles on blur:
// clamping on every keystroke would turn a half-typed "1" into the 20-page
// minimum before the "50" arrives.
function NumberField({ cat, value, onCommit, note }) {
  const [draft, setDraft] = useState(value == null ? '' : String(value));

  useEffect(() => {
    setDraft(value == null ? '' : String(value));
  }, [value]);

  const inRange = (n) => !Number.isNaN(n) && n >= cat.min && n <= cat.max;

  return (
    <Field
      label={cat.name}
      type="number"
      value={draft}
      onChange={(e) => {
        setDraft(e.target.value);
        const n = parseInt(e.target.value, 10);
        if (inRange(n)) onCommit(n);
      }}
      onBlur={() => {
        const n = parseInt(draft, 10);
        const settled = Number.isNaN(n) ? cat.min : Math.min(cat.max, Math.max(cat.min, n));
        setDraft(String(settled));
        if (settled !== value) onCommit(settled);
      }}
      inputProps={{ min: cat.min, max: cat.max, step: 1, 'data-testid': `intake-spec-${cat.id}` }}
      helperText={note || `${cat.min.toLocaleString('en-US')} to ${cat.max.toLocaleString('en-US')}`}
    />
  );
}

export function QuoteStrip({ quote, error, label = 'Estimated print total' }) {
  const discount = quote && quote.discount_percent > 0 ? ` · ${quote.discount_percent}% volume discount` : '';
  return (
    <Box
      sx={{
        mt: 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 1.25,
        background: tokens.color.plum,
        borderRadius: '7px',
        p: '16px 20px',
        color: SP.white,
      }}
      data-testid="intake-quote"
    >
      <Box>
        <Typography
          sx={{ fontFamily: FONT.mono, fontSize: 10.5, letterSpacing: '.05em', textTransform: 'uppercase', color: tokens.color.onDark }}
        >
          {label}
        </Typography>
        <Typography sx={{ fontSize: 11.5, color: tokens.color.onDark, mt: '2px' }}>
          {error ||
            (quote
              ? `For ${quote.quantity.toLocaleString('en-US')} ${quote.quantity === 1 ? 'copy' : 'copies'} · setup fee included${discount}`
              : 'Working it out…')}
        </Typography>
      </Box>
      <Typography sx={{ fontFamily: FONT.serif, fontSize: 24, fontWeight: 600, color: SP.goldLight }}>
        {quote ? money(quote.total) : '—'}
      </Typography>
    </Box>
  );
}

export default function SpecsForm({
  pricing,
  specs,
  onChange,
  email,
  onEmailChange,
  emailTouched,
  onEmailBlur,
  pagesNote,
  quote,
  quoteError,
  showEmail = true,
}) {
  const set = (id, value) => onChange({ ...specs, [id]: value });
  const emailInvalid = emailTouched && !isValidEmail(email);

  return (
    <>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 3 }}>
        {showEmail && (
          <Box sx={{ gridColumn: '1 / -1' }}>
            <Field
              label="Contact email"
              type="email"
              required
              value={email}
              onChange={(e) => onEmailChange(e.target.value)}
              onBlur={onEmailBlur}
              error={emailInvalid ? 'Enter a valid email address.' : undefined}
              helperText="Where we can reach you about this book."
              inputProps={{ 'data-testid': 'intake-email', autoComplete: 'email' }}
            />
          </Box>
        )}
        {(pricing.categories || []).map((cat) =>
          cat.type === 'number' ? (
            <NumberField
              key={cat.id}
              cat={cat}
              value={specs[cat.id]}
              onCommit={(n) => set(cat.id, n)}
              note={cat.id === 'pages' ? pagesNote : undefined}
            />
          ) : (
            <Field
              key={cat.id}
              select
              label={cat.name}
              value={specs[cat.id] ?? ''}
              onChange={(e) => set(cat.id, e.target.value)}
              inputProps={{ 'data-testid': `intake-spec-${cat.id}` }}
            >
              {(cat.options || []).map((o) => (
                <MenuItem key={o.id} value={o.id}>
                  {o.name}
                </MenuItem>
              ))}
            </Field>
          )
        )}
      </Box>
      <QuoteStrip quote={quote} error={quoteError} />
    </>
  );
}
